import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Tooltip,
    Legend,
    Title,
} from "chart.js";

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Title);

const LineCharts: React.FC = () => {
    const [data, setData] = useState<any>(null);
    const [question, setQuestion] = useState<string>("");
    const [correctAnswer, setCorrectAnswer] = useState<string>("");
    const [options, setOptions] = useState<string[]>([]);
    const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
    const [error, setError] = useState(false);
    const [showImage, setShowImage] = useState(false);

    // Generate random line chart data
    const generateLineChartData = () => {
        const labels = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
        const values = Array.from({ length: 5 }, () => Math.floor(Math.random() * 20) + 1);

        return {
            labels,
            datasets: [
                {
                    label: "Toy Sales",
                    data: values,
                    borderColor: "#36A2EB",
                    backgroundColor: "rgba(54, 162, 235, 0.2)",
                    tension: 0.4,
                    fill: true,
                },
            ],
            rawData: values,
        };
    };

    // Generate a random question and its correct answer
    const generateQuestionAndAnswer = (values: number[]) => {
        const highestValue = Math.max(...values);
        const total = values.reduce((sum, value) => sum + value, 0);
        const randomIndex = Math.floor(Math.random() * 3) + 1;

        const questionOptions = [
            { question: `What is the highest number of toys sold?`, answer: highestValue.toString() },
            { question: `What is the total number of toys sold?`, answer: total.toString() },
            { question: `How many toys were sold on ${data?.labels[randomIndex]}?`, answer: values[randomIndex].toString() },
        ];

        const randomQuestion = questionOptions[Math.floor(Math.random() * questionOptions.length)];
        return randomQuestion;
    };

    // Generate answer options with one correct and three random incorrect options
    const generateAnswerOptions = (correct: string, values: number[]) => {
        const optionsSet = new Set<string>();
        optionsSet.add(correct);

        while (optionsSet.size < 4) {
            const randomOption = (Math.floor(Math.random() * 20) + 1).toString();
            if (!optionsSet.has(randomOption)) {
                optionsSet.add(randomOption);
            }
        }

        return Array.from(optionsSet).sort(() => Math.random() - 0.5);
    };

    const handleSubmitAnswer = (option: any) => {
        setSelectedAnswer(option)
        if (option === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                handleNewQuestion();
                setShowImage(false);
                setSelectedAnswer(null);
                setError(false);
            }, 3000);
        } else {
            setError(true);
        }
    };

    const handleNewQuestion = () => {
        const chartData = generateLineChartData();
        const { question, answer } = generateQuestionAndAnswer(chartData.rawData);
        const answerOptions = generateAnswerOptions(answer, chartData.rawData);

        setData(chartData);
        setQuestion(question);
        setCorrectAnswer(answer);
        setOptions(answerOptions);
        setSelectedAnswer(null);
    };

    useEffect(() => {
        handleNewQuestion();
        document.title = "Mamas Guidance - Class One - Mathematics - Data Handling - Line Charts"; // Set page title
    }, []);

    const chartOptions = {
        plugins: {
            legend: { display: false },
            tooltip: { bodyFont: { size: 20 } },
            datalabels: { color: "#000", font: { size: 16 } },
        },
        scales: {
            x: { ticks: { font: { size: 14 } } },
            y: { ticks: { font: { size: 14 }, beginAtZero: true } },
        },
    };

    return (
        <>
            {showImage === false ? (
                <div className="contentPart showCenter additionWord">
                    <div className="pieChartDiv">
                        {data && (
                            <>
                                <div className="pieChart">
                                    <Line options={chartOptions} data={data} height={300} width={300} />
                                </div>
                                <div className="pieChartQuestionPart mt-3">
                                    <h3>{question}</h3>
                                    <div className="options-container" style={{ margin: "20px 0", display: "flex", "flexWrap": "wrap", "gap": "30px" }}>
                                        {options.map((option, index) => (
                                            <div
                                                key={index}
                                                className={`background-blue ${selectedAnswer === option ? "selected" : ""}`}
                                                onClick={(e) => handleSubmitAnswer(option)}
                                                style={{ width: 'auto', padding: '10px', fontSize: '30px' }}
                                            >
                                                {option}
                                            </div>
                                        ))}
                                    </div>
                                    {error && <p className="error-text">❌ Oops! Try again.</p>}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_WELDONE_LINK} alt="Well Done" />
                </div>
            )}
        </>
    );
};

export default LineCharts;
